import React from "react";
import { X, FileText } from "lucide-react";
import { PrincipalFormData, PrincipalFormErrors } from "../types";
import { useLobRegion } from "../../../../hooks/useLobRegion";

interface VoucherLocationFormProps {
  formData: PrincipalFormData;
  formErrors: PrincipalFormErrors;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onContractDocumentChange: (file: File | null) => void;
  onRemoveContractDocument: () => void;
}

const VoucherLocationForm: React.FC<VoucherLocationFormProps> = ({
  formData,
  formErrors,
  onChange,
  onContractDocumentChange,
  onRemoveContractDocument,
}) => {
  const region = useLobRegion();
  return (
    <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
      <h2 className="text-lg font-semibold mb-4">
        Voucher & Location Information
      </h2>
      <div className="space-y-6">
        {/* Voucher Type and Country Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {region.indiaFields && (
          <div>
            <label htmlFor="voucherType" className="block text-sm font-medium text-gray-700 mb-2">
              Voucher Type <span className="text-red-500">*</span>
            </label>
            <select
              id="voucherType"
              name="voucherType"
              value={formData.voucherType}
              onChange={onChange}
              className={`block w-full px-3 py-2 border ${formErrors.voucherType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand appearance-none`}
            >
              <option value="erupi">ERUPI</option>
              <option value="other">Other</option>
            </select>
            {formErrors.voucherType && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.voucherType}
              </div>
            )}
          </div>
          )}

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
              Country <span className="text-red-500">*</span>
            </label>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={onChange}
              className={`block w-full px-3 py-2 border ${formErrors.country ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand appearance-none`}
            >
              {region.indiaFields ? (
                <>
                  <option value="IND">India</option>
                  <option value="USA">United States</option>
                  <option value="GBR">United Kingdom</option>
                </>
              ) : (
                <option value={region.country}>{region.countryName}</option>
              )}
            </select>
            {formErrors.country && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.country}
              </div>
            )}
          </div>
        </div>

        {/* Payment Term and Contract Document URL Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="paymentTerm" className="block text-sm font-medium text-gray-700 mb-2">
              Payment Term
            </label>
            <select
              id="paymentTerm"
              name="paymentTerm"
              value={formData.paymentTerm}
              onChange={onChange}
              className={`block w-full px-3 py-2 border ${formErrors.paymentTerm ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand appearance-none`}
            >
              <option value="">Select Payment Term</option>
              <option value="NET30">NET30</option>
              <option value="NET60">NET60</option>
              <option value="NET90">NET90</option>
              <option value="IMMEDIATE">Immediate</option>
            </select>
            {formErrors.paymentTerm && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.paymentTerm}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="contractDocumentUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Contract Document
            </label>
            {/* Document Preview/Info */}
            {formData.contractDocumentUrl && (
              <div className="mb-2 p-3 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-600" />
                  <span className="text-sm text-gray-700">{formData.contractDocumentUrl.name}</span>
                  <span className="text-xs text-gray-500">({(formData.contractDocumentUrl.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onRemoveContractDocument();
                    const fileInput = document.getElementById("contractDocumentFile") as HTMLInputElement;
                    if (fileInput) fileInput.value = "";
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <input
              type="file"
              id="contractDocumentFile"
              accept=".pdf,.doc,.docx"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                onContractDocumentChange(file || null);
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
            />
            <p className="text-sm text-gray-500 mt-1">
              Optional: Upload contract document (Max 5MB)
            </p>
            {formErrors.contractDocumentUrl && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.contractDocumentUrl}
              </div>
            )}
          </div>
        </div>

        {/* Distribution Only Checkbox */}
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="isDistributionOnly"
              checked={formData.isDistributionOnly}
              onChange={onChange}
              className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
            />
            <span className="text-sm font-medium text-gray-700">
              Distribution Only
            </span>
          </label>
          <p className="text-sm text-gray-500 mt-1 ml-6">
            Check this if this principal is for distribution only
          </p>
        </div>
      </div>
    </div>
  );
};

export default VoucherLocationForm;
